/*
  CBG Quick View — collection page popup controller.

  One <cbg-quick-view> shell lives in section-cbg-collection-grid.liquid, OUTSIDE the AJAX-swapped
  #CbgProductGridContainer, so it survives facet filtering / view switching / pagination.

  Flow:
   - A card's [data-cbg-qv-open] button (delegated on the persistent grid container) opens the modal.
   - We fetch the product's Quick View fragment via the Section Rendering API + the fetch-only
     alternate template:  {product.url}?view=quick-view&section_id=main
     and inject [data-cbg-qv-content] into the dialog. This works whether the response is the
     isolated section fragment OR (fallback) a full page — we extract the same node either way.
   - Add to cart posts /cart/add.js and publishes cartUpdate so the mini-cart stays in sync
     (same mechanism as the PDP; no edits to Dawn's product-form.js or globals).
   - Any fetch failure degrades gracefully by navigating to the product page.

  Relies on Dawn globals when present (routes / PUB_SUB_EVENTS / publish), referenced defensively.
*/
if (!customElements.get('cbg-quick-view')) {
  class CbgQuickView extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.cbg-qv__dialog');
      this.body = this.querySelector('[data-cbg-qv-body]');
      this.spinner = this.querySelector('[data-cbg-qv-spinner]');
      this.addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      this.lastFocus = null;
      this.currentUrl = null;
      this.controller = null;

      this.grid = document.getElementById('CbgProductGridContainer');

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onSelfClick = this.onSelfClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      /* Delegate on the persistent container (its innerHTML is swapped, the element itself stays). */
      (this.grid || document).addEventListener('click', this.onTriggerClick);
      this.addEventListener('click', this.onSelfClick);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      (this.grid || document).removeEventListener('click', this.onTriggerClick);
      this.removeEventListener('click', this.onSelfClick);
      this.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.controller) this.controller.abort();
    }

    /* ---------- Trigger ---------- */
    onTriggerClick(event) {
      const trigger = event.target.closest('[data-cbg-qv-open]');
      if (!trigger) return;
      event.preventDefault();
      const url = trigger.getAttribute('data-product-url');
      if (url) this.open(url);
    }

    /* ---------- In-modal clicks ---------- */
    onSelfClick(event) {
      /* Close on X / continue, or a click on the dim wrapper itself (dialog is a child). */
      if (event.target.closest('[data-cbg-qv-close]') || event.target === this) {
        this.close();
        return;
      }
      const step = event.target.closest('[data-cbg-qv-qty-plus], [data-cbg-qv-qty-minus]');
      if (step) this.step(step);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    }

    /* ---------- Open / fetch / inject ---------- */
    async open(url) {
      this.currentUrl = url;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      if (this.body) this.body.innerHTML = '';
      if (this.spinner) this.spinner.hidden = false;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const controller = this.controller;

      try {
        const response = await fetch(this.buildUrl(url), { signal: controller.signal });
        const text = await response.text();
        const node = this.extractContent(text);
        if (!node) {
          if (this.spinner) this.spinner.hidden = true;
          this.renderError();
          return;
        }
        if (this.body) this.body.appendChild(node);
        /* Stay on the loading state until the injected images have finished, so the dialog
           sizes itself once instead of stepping through every image load. */
        await this.waitForImages(node);
        /* Closed or reopened while we were waiting — that request owns the modal now. */
        if (controller.signal.aborted) return;
        if (this.spinner) this.spinner.hidden = true;
        node.classList.add('is-ready');
        this.focusDialog();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.warn('cbg-quick-view:', error);
        if (this.spinner) this.spinner.hidden = true;
        this.renderError();
      }
    }

    buildUrl(url) {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      /* Full alternate-template page; we extract the fragment from it. Reliable across stores
         (does not depend on the ?view= + ?section_id= combination behaving a particular way). */
      return `${url}${sep}view=quick-view`;
    }

    /* Resolves once every <img> inside `root` has settled (loaded or failed), or after the
       timeout — one stalled image must never leave the modal stuck on its spinner.
       NB: `root` is still display:none at this point (see .cbg-qv__content in the CSS). Eager
       images load fine in a hidden subtree; a loading="lazy" image added to the Quick View
       fragment would never load there and would only resolve on the timeout. */
    waitForImages(root, timeout = 4000) {
      const pending = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
      if (pending.length === 0) return Promise.resolve();

      const settled = Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      );

      return Promise.race([settled, new Promise((resolve) => setTimeout(resolve, timeout))]);
    }

    /* Finds the fragment whether the response is the isolated section OR a full page. */
    extractContent(text) {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      return doc.querySelector('[data-cbg-qv-content]');
    }

    /* Never auto-navigates — shows an in-modal message + a link to the product page. */
    renderError() {
      if (!this.body) return;
      const text = this.getAttribute('data-error-text') || '';
      const linkText = this.getAttribute('data-error-link') || '';
      const url = this.currentUrl || '#';
      this.body.innerHTML =
        '<div class="cbg-qv__error"><p class="cbg-qv__error-text">' +
        text +
        '</p><a class="cbg-qv__btn cbg-qv__btn--cart" href="' +
        url +
        '">' +
        linkText +
        '</a></div>';
      this.focusDialog();
    }

    focusDialog() {
      const closeBtn = this.querySelector('[data-cbg-qv-close]');
      if (closeBtn) closeBtn.focus();
    }

    /* ---------- Quantity ---------- */
    step(btn) {
      const control = btn.closest('[data-cbg-qv-qty]');
      if (!control) return;
      const input = control.querySelector('[data-cbg-qv-qty-input]');
      if (!input) return;
      const min = parseInt(control.dataset.min, 10) || 1;
      const current = parseInt(input.value, 10) || min;
      const next = Math.max(min, current + (btn.hasAttribute('data-cbg-qv-qty-plus') ? 1 : -1));
      input.value = next;
      this.clearQtyError(control);
    }

    clearQtyError(control) {
      const errEl = control.querySelector('[data-cbg-qv-qty-error]');
      const input = control.querySelector('[data-cbg-qv-qty-input]');
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = '';
      }
      if (input) input.removeAttribute('aria-invalid');
    }

    /* ---------- Add to cart ---------- */
    async onSubmit(event) {
      event.preventDefault();
      const form = event.target.closest('.cbg-qv__form');
      if (!form) return;

      const control = form.querySelector('[data-cbg-qv-qty]');
      const input = form.querySelector('[data-cbg-qv-qty-input]');
      const min = control ? parseInt(control.dataset.min, 10) || 1 : 1;
      const value = input ? parseInt(input.value, 10) || 0 : 1;
      if (control) this.clearQtyError(control);
      if (value < min) {
        this.showQtyError(control, control ? control.dataset.minError : '');
        return;
      }

      const addBtn = form.querySelector('[data-cbg-qv-add]');
      if (addBtn) addBtn.setAttribute('aria-disabled', 'true');

      try {
        const body = new FormData(form);
        /* Ask the add request to return the re-rendered header sections alongside the line,
           so the cart counter and the mini-cart dropdown update in the same round trip
           (see assets/cbg-cart-sync.js). */
        if (window.CbgCartSync) {
          body.append('sections', window.CbgCartSync.sectionsParam());
          body.append('sections_url', window.location.pathname);
        }
        const response = await fetch(this.addUrl, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: body,
        });
        const data = await response.json();
        if (data.status) {
          console.warn('cbg-quick-view add error:', data.description || data.message);
          this.notifyError(data);
          return;
        }
        this.syncCart(data);
        this.showAdded();
      } catch (error) {
        console.warn('cbg-quick-view add failed:', error);
      } finally {
        if (addBtn) addBtn.removeAttribute('aria-disabled');
      }
    }

    showQtyError(control, message) {
      if (!control) return;
      const errEl = control.querySelector('[data-cbg-qv-qty-error]');
      const input = control.querySelector('[data-cbg-qv-qty-input]');
      if (errEl && message) {
        errEl.textContent = message;
        errEl.hidden = false;
      }
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
    }

    /* Re-render the modal to the "Added To Cart" state (does NOT close). CSS shows/hides the two
       states off the .is-added class; here we also copy the chosen quantity into the static line. */
    showAdded() {
      if (!this.body) return;
      const content = this.body.querySelector('[data-cbg-qv-content]');
      if (!content) return;
      const input = content.querySelector('[data-cbg-qv-qty-input]');
      const addedQty = content.querySelector('[data-cbg-qv-added-qty]');
      if (input && addedQty) addedQty.textContent = input.value;
      content.classList.add('is-added');
    }

    /* Repaint the header cart UI (counter + mini-cart dropdown) from this response and
       publish cartUpdate for the rest of the theme. cbg-cart-sync.js owns both; if it is
       missing we fall back to the bare publish so nothing regresses. */
    syncCart(cartData) {
      if (window.CbgCartSync) {
        window.CbgCartSync.handleCartResponse('cbg-quick-view', cartData);
        return;
      }
      this.notifyUpdate(cartData);
    }

    notifyUpdate(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cbg-quick-view', cartData });
      }
    }

    notifyError(data) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cbg-quick-view',
          message: data.description || data.message,
        });
      }
    }

    /* ---------- Close ---------- */
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      /* Content is deliberately NOT cleared here — emptying it mid-fade-out would collapse the
         dialog on the way out. open() clears it before the next fetch, and while closed the
         subtree is visibility:hidden, so it is neither focusable nor clickable. */
      if (this.spinner) this.spinner.hidden = true;
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }
  }

  customElements.define('cbg-quick-view', CbgQuickView);
}
